import React from 'react';
import Zana, { ZanaState } from './Zana';

/** Shared Ask Zana chat chrome — dock and full-screen stay visually in sync. */

export const ZANA_CHAT_STYLES = `
@keyframes zanaBubbleIn {
  from { opacity: 0; transform: translateY(8px) scale(0.98); }
  to { opacity: 1; transform: translateY(0) scale(1); }
}
@keyframes zanaThinkDot {
  0%, 60%, 100% { opacity: 0.25; transform: translateY(0); }
  30% { opacity: 1; transform: translateY(-3px); }
}
@keyframes zanaThinkPulse {
  0%, 100% { box-shadow: 0 0 0 0 rgba(155,124,255,0); }
  50% { box-shadow: 0 0 0 4px rgba(155,124,255,.18); }
}
.zana-bubble-in { animation: zanaBubbleIn .32s cubic-bezier(.22,.95,.32,1) both; }
.zana-think-dot { animation: zanaThinkDot 1.2s ease-in-out infinite; }
.zana-think-dot:nth-child(2) { animation-delay: .15s; }
.zana-think-dot:nth-child(3) { animation-delay: .3s; }
.zana-think-bubble { animation: zanaThinkPulse 1.8s ease-in-out infinite; }
`;

export type ZanaMsgSource = 'ai' | 'deterministic' | 'cache' | 'static' | 'local';

const SOURCE_LABELS: Record<string, string> = {
  ai: 'Reasoned',
  deterministic: 'Live facts',
  cache: 'Recent answer',
  static: 'Instant',
  local: 'On-screen',
};

export function zanaSourceLabel(source?: string | null): string | null {
  if (!source) return null;
  return SOURCE_LABELS[source] || null;
}

export function ZanaThinkingBubble({
  compact = false,
  label = 'Thinking…',
}: {
  compact?: boolean;
  label?: string;
}) {
  return (
    <div
      className="zana-bubble-in zana-think-bubble"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: compact ? 8 : 10,
        padding: compact ? '9px 13px' : '11px 15px',
        borderRadius: compact ? '4px 16px 16px 16px' : 16,
        background: 'rgba(255,255,255,.08)',
        border: '1px solid rgba(155,124,255,.28)',
        maxWidth: '92%',
      }}
    >
      <Zana state="thinking" size={compact ? 22 : 28} animated />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
        <span style={{ fontSize: compact ? 12.5 : 13.5, fontWeight: 700, color: '#c4b3ff' }}>
          {label}
        </span>
        <span style={{ display: 'inline-flex', gap: 4, alignItems: 'center' }}>
          {[0, 1, 2].map((i) => (
            <span
              key={i}
              className="zana-think-dot"
              style={{
                width: 5,
                height: 5,
                borderRadius: '50%',
                background: '#9b7cff',
                display: 'inline-block',
              }}
            />
          ))}
        </span>
      </div>
    </div>
  );
}

export function ZanaMessageBubble({
  from,
  text,
  compact = false,
  source,
}: {
  from: 'you' | 'zana';
  text: string;
  compact?: boolean;
  source?: ZanaMsgSource | string | null;
}) {
  const isYou = from === 'you';
  const badge = !isYou ? zanaSourceLabel(source) : null;
  /* THE WIDTH CAP CANNOT LIVE HERE.
     ──────────────────────────────────────────────────────────────────────────
     This was `maxWidth: compact ? '92%' : '86%'`, and both callers render the
     bubble inside a shrink-to-fit wrapper — a flex item on the page, an
     `alignSelf` child in the dock. A percentage resolves against that wrapper,
     and the wrapper is sized by this bubble's own text. So every message came
     out 86% as wide as the space it had just asked for, left-aligned inside
     the remaining 14%.
     On the right-hand side that gap is visible: each of the owner's messages
     sat in from the right edge by 14% of ITS OWN natural width, so a long
     question and a short one stopped at different places and nothing lined up.
     It also made one-line questions wrap onto two, since the text was being
     wrapped at 86% of the width it needed. Gabriel, 2026-08-29: "why aren't
     the messages aligned".
     The bubble now fills whatever it is given; the CAP moved out to the
     wrapper, where the percentage has a real box to resolve against. */
  return (
    <div className="zana-bubble-in" style={{ maxWidth: '100%', minWidth: 0 }}>
      <div
        style={{
          fontSize: compact ? 13.5 : 14,
          lineHeight: compact ? 1.45 : 1.62,
          padding: compact ? '9px 13px' : '12px 15px',
          borderRadius: isYou
            ? (compact ? '16px 16px 4px 16px' : '16px 16px 6px 16px')
            : (compact ? '4px 16px 16px 16px' : '6px 16px 16px 16px'),
          background: isYou ? '#6f4ff0' : (compact ? 'rgba(255,255,255,.08)' : 'rgba(255,255,255,.05)'),
          border: isYou ? 'none' : (compact ? '1px solid rgba(255,255,255,.12)' : '1px solid rgba(155,124,255,.28)'),
          color: isYou ? '#fff' : '#efe9ff',
          whiteSpace: 'pre-wrap',
          transition: 'background .2s ease, border-color .2s ease',
        }}
      >
        {text}
      </div>
      {badge && (
        <div style={{ marginTop: 5, fontSize: 10, fontWeight: 700, letterSpacing: '.06em', color: '#8f86b6', textTransform: 'uppercase' }}>
          {badge}
        </div>
      )}
    </div>
  );
}

export function ZanaContextBar({
  turnCount,
  onOpenHistory,
  historyCount = 0,
  compact = false,
}: {
  turnCount: number;
  onOpenHistory?: () => void;
  historyCount?: number;
  compact?: boolean;
}) {
  if (turnCount <= 0) return null;
  const kept = Math.min(turnCount, 6);
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 8,
        flexWrap: 'wrap',
        padding: compact ? '0 16px 8px' : '0 20px 10px',
        fontSize: compact ? 10.5 : 11.5,
        color: '#a898db',
      }}
    >
      <span>
        She keeps the last {kept} message{kept === 1 ? '' : 's'} in this thread for follow-ups.
        {' '}Fleet notes from earlier chats still apply.
      </span>
      {onOpenHistory && historyCount > 0 && (
        <button
          type="button"
          onClick={onOpenHistory}
          style={{
            border: 'none',
            background: 'transparent',
            color: '#c4b3ff',
            fontSize: 'inherit',
            fontWeight: 800,
            cursor: 'pointer',
            padding: 0,
            textDecoration: 'underline',
          }}
        >
          Past chats ({historyCount})
        </button>
      )}
    </div>
  );
}
